import { Quote, ArrowUpRight, Phone, Cpu, Flame } from "lucide-react";
import { Link } from "@/components/common/Link";
const NAVY = "#081438";
const NAVY_LIGHT = "#243752";
const NAVY_DEEP = "#040A1F";
const GOLD = "#F5A623";
const FONT_HEAD = "'Plus Jakarta Sans', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
const FONT_MONO = "ui-monospace, 'SF Mono', 'JetBrains Mono', Menlo, monospace";
type Brand = {
  monogram: string;
  name: string;
  tagline: string;
  models: string[];
  knownFor: string;
};
const FORZA: Brand = {
  monogram: "F",
  name: "BOSS Forza",
  tagline: "Italian-designed · Boss Operators Australia",
  models: ["Forza 1200", "Forza 1200W", "Forza PanelDor", "Forza TiltDor", "Forza Sectional", "Forza Belt Drive", "Forza Chain Drive"],
  knownFor: "LCD control board failure, belt slip on the C-rail drive, encoder/limit drift, carriage wear. By far the most replaced opener we see in 2025–2026."
};
const BRANDS: Brand[] = [{
  monogram: "M",
  name: "Merlin SuperLift",
  tagline: "Chamberlain Group · Australia's most installed",
  models: ["MT60 SilentDrive", "MT60EVO", "MT100 Commercial", "MR655MYQ", "MJ3800MYQ", "MJ5580MYQ", "E850M / E940M", "Tiltmaster MT60P", "Commander 230T"],
  knownFor: "Stripped plastic main gears, MyQ wifi dropouts, capacitor failure after 8–10 yrs."
}, {
  monogram: "A",
  name: "ATA",
  tagline: "Australian designed · Sectional, roller & tilt",
  models: ["GDO-6v3 NeoSlider", "GDO-6v4 EasyRoller", "GDO-7v3 EasyRoller", "GDO-9v3 SecuraLift", "GDO-10v1 SecuraLift Pro", "GDO-11v1 GuardianPro", "DCB-05 Control"],
  knownFor: "Travel limit drift, remote receiver failure, control board moisture damage."
}, {
  monogram: "S",
  name: "Steel-Line",
  tagline: "Sydney's residential favourite · Sectional",
  models: ["SmartLift SDO-1", "SmartLift 800", "SmartLift 1000", "Heritage Opener", "Garage Master", "Drive Series 800/1000"],
  knownFor: "Worn drive belts, sensor misalignment, motor brushes on older units."
}, {
  monogram: "D",
  name: "DoorWorks",
  tagline: "Heavy-duty residential & light commercial",
  models: ["Force II", "HD8", "HD10 Series", "SmartLift", "Sportlift", "PowerLift"],
  knownFor: "Limit switch wear, control board faults, chain & belt tensioner issues."
}, {
  monogram: "B",
  name: "B&D",
  tagline: "Iconic Australian brand · Long-running units",
  models: ["Controll-A-Door 5 (CAD5)", "Controll-A-Door 4 (CAD4)", "CAD-DT Digital", "Powerdrive", "TB6 / TB7 Easylifter", "Sectional Drive 800"],
  knownFor: "End-of-life motor failure, remote receiver issues, blown capacitors on legacy CAD."
}, {
  monogram: "C",
  name: "Chamberlain / LiftMaster",
  tagline: "US-engineered · Premium home & commercial",
  models: ["LiftMaster 8500W", "LiftMaster 8550W", "Whisper Drive 248730B", "Premium 8557W", "MyQ Smart 8587W"],
  knownFor: "MyQ connectivity dropouts, force calibration drift, gear wear after heavy cycling."
}];
export default function WhyChooseMotor() {
  return <section className="relative overflow-hidden" style={{
    backgroundColor: NAVY,
    fontFamily: FONT_BODY
  }} aria-labelledby="why-choose-motor-heading">
      {/* Background */}
      <div aria-hidden className="absolute inset-0 pointer-events-none" style={{
      background: `radial-gradient(ellipse at 15% 30%, ${NAVY_LIGHT}66 0%, transparent 50%), radial-gradient(ellipse at 85% 80%, ${NAVY_DEEP} 0%, transparent 60%)`
    }} />

      {/* Grain */}
      <div aria-hidden className="absolute inset-0 opacity-[0.05] mix-blend-overlay pointer-events-none" style={{
      backgroundImage: "url(\"data:image/svg+xml;utf8,<svg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.9 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")"
    }} />

      {/* Top gold scanline */}
      <div aria-hidden className="absolute top-0 left-0 right-0 h-px" style={{
      background: `linear-gradient(90deg, transparent 0%, ${GOLD}66 50%, transparent 100%)`
    }} />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10 pt-20 md:pt-28 pb-20 md:pb-24">

        {/* Top strip */}
        <div className="flex items-center justify-between gap-6 mb-10 md:mb-12 flex-wrap">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full border" style={{
            borderColor: `${GOLD}66`,
            backgroundColor: `${GOLD}14`
          }}>
              <Cpu className="h-3.5 w-3.5" strokeWidth={2.5} style={{
              color: GOLD
            } as React.CSSProperties} />
            </span>
            <span className="text-[11px] font-bold tracking-[0.28em] uppercase" style={{
            color: GOLD
          }}>
              Brands &amp; Models We Service
            </span>
            <span className="hidden md:block h-px w-12" style={{
            backgroundColor: `${GOLD}40`
          }} />
          </div>
          <div className="text-[10px] font-semibold tracking-[0.22em] uppercase" style={{
          color: "rgba(255,255,255,0.4)"
        }}>
            7 Brands · 45+ Common Models
          </div>
        </div>

        {/* Compact header: H2 left, manifesto + signature right */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-10 md:mb-12 items-start">
          <div className="lg:col-span-7">
            <div className="flex items-stretch gap-4">
              <div aria-hidden className="w-[3px] flex-shrink-0" style={{
              background: `linear-gradient(180deg, ${GOLD} 0%, ${GOLD}26 100%)`
            }} />
              <div>
                <Quote className="h-7 w-7 mb-2" strokeWidth={1.5} style={{
                color: GOLD
              } as React.CSSProperties} />
                <h2 id="why-choose-motor-heading" className="font-bold text-white" style={{
                fontFamily: FONT_HEAD,
                fontSize: "clamp(1.75rem, 3.6vw, 2.75rem)",
                lineHeight: 1.1,
                letterSpacing: "-0.025em"
              }}>
                  We&rsquo;ve{" "}
                  <span style={{
                  color: GOLD
                }}>seen your opener</span> before.
                </h2>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5">
            <p className="text-[15px] leading-[1.6] mb-5" style={{
            color: "rgba(255,255,255,0.72)"
          }}>
              We stock the parts &mdash; gears, capacitors, control boards,
              remotes, sensors &mdash; for every major brand below. Know your
              model? Tell us when you call. Don&rsquo;t? We&rsquo;ll identify
              it on the spot.
            </p>

            <a href="tel:0485000933" className="group inline-flex items-center gap-3 text-[14px] font-bold transition-colors" style={{
            color: "white",
            fontFamily: FONT_BODY
          }} onMouseEnter={e => e.currentTarget.style.color = GOLD} onMouseLeave={e => e.currentTarget.style.color = "white"}>
              <span className="inline-flex items-center justify-center w-9 h-9 rounded-full border" style={{
              borderColor: `${GOLD}66`,
              backgroundColor: `${GOLD}1F`,
              color: GOLD
            }}>
                <Phone className="h-4 w-4" strokeWidth={2.5} />
              </span>
              Tell us your model &mdash; 0485 000 933
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.5} />
            </a>
          </div>
        </div>

        {/* Section label */}
        <div className="text-[10px] font-bold tracking-[0.32em] uppercase mb-6" style={{
        color: "rgba(255,255,255,0.4)"
      }}>
          &mdash; Models we fix most often
        </div>

        {/* === Forza featured card (full width, horizontal layout) === */}
        <FeaturedForzaCard brand={FORZA} />

        {/* === 2-col grid for remaining 6 brands === */}
        <div className="mt-5 md:mt-6 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {BRANDS.map((brand, idx) => <CompactBrandCard key={brand.name} brand={brand} index={idx + 2} />)}
        </div>
      </div>

      {/* Bottom gold scanline */}
      <div aria-hidden className="absolute bottom-0 left-0 right-0 h-px" style={{
      background: `linear-gradient(90deg, transparent 0%, ${GOLD}66 50%, transparent 100%)`
    }} />
    </section>;
}

/* === FEATURED FORZA CARD === */
function FeaturedForzaCard({
  brand
}: {
  brand: Brand;
}) {
  return <article className="relative rounded-2xl border overflow-hidden transition-all duration-300" style={{
    backgroundColor: "rgba(245,166,35,0.05)",
    borderColor: `${GOLD}40`,
    boxShadow: `0 16px 50px -20px ${GOLD}33`
  }}>
      {/* Top gold accent */}
      <div aria-hidden className="absolute top-0 left-0 right-0 h-[3px]" style={{
      background: `linear-gradient(90deg, ${GOLD} 0%, ${GOLD}80 50%, transparent 100%)`
    }} />

      {/* Big watermark "01" */}
      <div aria-hidden className="absolute -bottom-8 -right-2 font-extrabold leading-none select-none pointer-events-none" style={{
      fontFamily: FONT_HEAD,
      color: `${GOLD}14`,
      fontSize: "clamp(8rem, 14vw, 11rem)",
      letterSpacing: "-0.05em",
      fontVariantNumeric: "tabular-nums"
    }}>
        01
      </div>

      <div className="relative p-5 md:p-7 grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-7">

        {/* LEFT: monogram + name + failures */}
        <div className="md:col-span-5 flex flex-col">
          <div className="flex items-start gap-4 mb-4">
            <div className="flex-shrink-0 flex items-center justify-center w-14 h-14 rounded-xl font-extrabold" style={{
            backgroundColor: GOLD,
            color: NAVY,
            fontFamily: FONT_HEAD,
            fontSize: "1.625rem",
            letterSpacing: "-0.02em",
            boxShadow: `0 8px 24px -8px ${GOLD}80`
          }}>
              {brand.monogram}
            </div>

            <div className="flex-1 min-w-0">
              <h3 className="font-extrabold text-white mb-1" style={{
              fontFamily: FONT_HEAD,
              fontSize: "clamp(1.25rem, 1.9vw, 1.5rem)",
              lineHeight: 1.15,
              letterSpacing: "-0.02em"
            }}>
                {brand.name}
              </h3>

              <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px] font-extrabold tracking-[0.18em] uppercase mb-2" style={{
              backgroundColor: GOLD,
              color: NAVY,
              fontFamily: FONT_BODY
            }}>
                <Flame className="h-2.5 w-2.5" strokeWidth={2.5} />
                Most Replaced · 2026
              </span>

              <p className="text-[12px] font-medium" style={{
              color: "rgba(255,255,255,0.6)",
              fontFamily: FONT_BODY
            }}>
                {brand.tagline}
              </p>
            </div>
          </div>

          {/* Failures block */}
          <div className="rounded-lg overflow-hidden border mt-auto" style={{
          backgroundColor: "rgba(8,20,56,0.45)",
          borderColor: `${GOLD}33`
        }}>
            <div className="flex items-stretch">
              <div aria-hidden className="w-[3px] flex-shrink-0" style={{
              backgroundColor: GOLD
            }} />
              <div className="px-4 py-3">
                <div className="text-[9.5px] font-bold tracking-[0.22em] uppercase mb-1" style={{
                color: GOLD
              }}>
                  Common failures we see
                </div>
                <p className="text-[12.5px] leading-[1.55] font-medium" style={{
                color: "rgba(255,255,255,0.82)"
              }}>
                  {brand.knownFor}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT: models grid */}
        <div className="md:col-span-7">
          <div className="flex items-center justify-between gap-2 mb-3">
            <span className="text-[10px] font-bold tracking-[0.22em] uppercase" style={{
            color: GOLD
          }}>
              Models we fix
            </span>
            <span className="text-[10px] font-bold tracking-[0.16em] uppercase" style={{
            color: "rgba(255,255,255,0.35)"
          }}>
              {brand.models.length} units
            </span>
          </div>

          <div className="flex flex-wrap gap-1.5">
            {brand.models.map(model => <span key={model} className="text-[12px] font-semibold rounded-md px-2.5 py-1.5 border" style={{
            backgroundColor: "rgba(255,255,255,0.05)",
            borderColor: "rgba(245,166,35,0.20)",
            color: "rgba(255,255,255,0.92)",
            fontFamily: FONT_MONO,
            letterSpacing: "0.01em"
          }}>
                {model}
              </span>)}
          </div>
        </div>
      </div>
    </article>;
}

/* === COMPACT BRAND CARD === */
function CompactBrandCard({
  brand,
  index
}: {
  brand: Brand;
  index: number;
}) {
  return <article className="group relative rounded-2xl border overflow-hidden transition-all duration-300" style={{
    backgroundColor: "rgba(255,255,255,0.025)",
    borderColor: "rgba(255,255,255,0.08)"
  }} onMouseEnter={e => {
    e.currentTarget.style.borderColor = `${GOLD}66`;
    e.currentTarget.style.backgroundColor = "rgba(245,166,35,0.04)";
  }} onMouseLeave={e => {
    e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)";
    e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.025)";
  }}>
      {/* Hover top accent */}
      <div aria-hidden className="absolute top-0 left-0 right-0 h-px opacity-0 transition-opacity duration-300 group-hover:opacity-100" style={{
      background: `linear-gradient(90deg, transparent 0%, ${GOLD} 50%, transparent 100%)`
    }} />

      {/* Watermark number */}
      <div aria-hidden className="absolute -bottom-3 -right-1 font-extrabold leading-none select-none pointer-events-none" style={{
      fontFamily: FONT_HEAD,
      color: `${GOLD}0F`,
      fontSize: "clamp(4rem, 7vw, 5.5rem)",
      letterSpacing: "-0.04em",
      fontVariantNumeric: "tabular-nums"
    }}>
        {String(index).padStart(2, "0")}
      </div>

      <div className="relative p-4 md:p-5">

        {/* Header */}
        <div className="flex items-start gap-3 mb-3.5">
          <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-lg font-extrabold transition-all duration-300 group-hover:scale-105" style={{
          backgroundColor: `${GOLD}1F`,
          color: GOLD,
          border: `1px solid ${GOLD}40`,
          fontFamily: FONT_HEAD,
          fontSize: "1.0625rem",
          letterSpacing: "-0.02em"
        }}>
            {brand.monogram}
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="font-extrabold text-white transition-colors duration-200 group-hover:text-[#F5A623]" style={{
            fontFamily: FONT_HEAD,
            fontSize: "1.0625rem",
            lineHeight: 1.2,
            letterSpacing: "-0.018em"
          }}>
              {brand.name}
            </h3>
            <p className="text-[11.5px] font-medium mt-0.5 leading-snug" style={{
            color: "rgba(255,255,255,0.5)",
            fontFamily: FONT_BODY
          }}>
              {brand.tagline}
            </p>
          </div>

          <ArrowUpRight className="flex-shrink-0 mt-1 h-4 w-4 opacity-25 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.5} style={{
          color: GOLD
        } as React.CSSProperties} />
        </div>

        {/* Models row */}
        <div className="mb-3.5">
          <div className="flex items-center justify-between gap-2 mb-2">
            <span className="text-[9.5px] font-bold tracking-[0.2em] uppercase" style={{
            color: GOLD
          }}>
              Models
            </span>
            <span className="text-[9.5px] font-bold tracking-[0.16em] uppercase" style={{
            color: "rgba(255,255,255,0.3)"
          }}>
              {brand.models.length} units
            </span>
          </div>

          <div className="flex flex-wrap gap-1">
            {brand.models.map(model => <span key={model} className="text-[10.5px] font-semibold rounded px-1.5 py-0.5 border" style={{
            backgroundColor: "rgba(255,255,255,0.04)",
            borderColor: "rgba(255,255,255,0.10)",
            color: "rgba(255,255,255,0.82)",
            fontFamily: FONT_MONO
          }}>
                {model}
              </span>)}
          </div>
        </div>

        {/* Failures - inline compact */}
        <div className="flex items-start gap-2.5">
          <span aria-hidden className="flex-shrink-0 mt-1 inline-block w-1 h-3 rounded-full" style={{
          backgroundColor: GOLD
        }} />
          <p className="text-[11.5px] leading-[1.55] font-medium" style={{
          color: "rgba(255,255,255,0.65)"
        }}>
            {brand.knownFor}
          </p>
        </div>
      </div>
    </article>;
}